// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OccViewer } from "../../../core/viewer/viewer.js";
import { MedicalUtils } from "../../../modules/medical/utils/utils.js";
import { cppToTsVector } from "../../../core/types/type-converter.js";
import { wrapIPromise } from "../../../core/async/promise.js";
import { getMedical } from "../medical.js";
/**
 * Tool for collision calculations.
 */
export class CollisionUtils {
    /** Constructor. */
    constructor() {
        this._myCollision = new Map();
    }
    /**
     * Add collision data for object
     * @param theId id of object
     * @param thePath path to the gzip collision data
     */
    addCollisionData(theId, thePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const aBuffer = yield fetch(thePath).then((response) => response.arrayBuffer());
            yield this.addCollisionDataFromMemory(theId, aBuffer);
        });
    }
    /**
     * Add collision data for object
     * @param theId id of object
     * @param theBuffer data buffer
     */
    addCollisionDataFromMemory(theId, theBuffer) {
        return __awaiter(this, void 0, void 0, function* () {
            let aDataArray = new Uint8Array(theBuffer);
            //@ts-ignore
            const aDataBuffer = CollisionUtils._myViewer["_malloc"](aDataArray.length);
            //@ts-ignore
            CollisionUtils._myViewer["HEAPU8"].set(aDataArray, aDataBuffer.byteOffset);
            try {
                yield wrapIPromise((aPromise) => {
                    CollisionUtils._myCollisionUtils.setCollisionData(theId, aDataBuffer.byteOffset, aDataArray.length, aPromise);
                });
            }
            catch (e) {
                throw e;
            }
            finally {
                //! Don't forget to free memory
                //@ts-ignore
                CollisionUtils._myViewer["_free"](aDataBuffer.byteOffset);
            }
        });
    }
    /**
     * Remove collision data from object
     * @param theId id of object
     */
    removeCollisionData(theId) {
        CollisionUtils._myCollisionUtils.removeCollisionData(theId);
    }
    /**
     * Erase collision data from viewer
     * @param theId id of object
     */
    eraseCollisionData(theId) {
        CollisionUtils._myCollisionUtils.eraseCollisionData(theId);
    }
    /**
     * Set custom color for collision triangles.
     * @param theId id of object
     * @param theActivity name of activity
     * @param theStep step number
     * @param theColor custom color
     * @param theIsReplace flag indicates the need to replace or combine collision data
     */
    setCollisionPerStep(theId, theActivity, theStep, theColor, theIsReplace = true) {
        CollisionUtils._myCollisionUtils.setCollisionPerStep(theId, theActivity, theStep, theColor, theIsReplace);
    }
    /**
     * Set custom color for collision triangles.
     * @param theId id of object
     * @param theActivity name of activity
     * @param theColor custom color
     * @param theIsReplace flag indicates the need to replace or combine collision data
     */
    setCollisionPerActivity(theId, theActivity, theColor, theIsReplace = true) {
        CollisionUtils._myCollisionUtils.setCollisionPerActivity(theId, theActivity, theColor, theIsReplace);
    }
    /**
     * Set custom color for collision triangles.
     * @param theId id of object
     * @param theColor custom color
     * @param theIsReplace flag indicates the need to replace or combine collision data
     */
    setCollision(theId, theColor, theIsReplace = true) {
        CollisionUtils._myCollisionUtils.setCollision(theId, theColor, theIsReplace);
    }
    /**
     * Returns true if object has collision on the step
     * @param theId id of object
     * @param theActivity name of activity
     * @param theStep step number
     */
    hasCollisionPerStep(theId, theActivity, theStep) {
        return CollisionUtils._myCollisionUtils.hasCollisionPerStep(theId, theActivity, theStep);
    }
    /**
     * Returns true if object has collision for an activity
     * @param theId id of object
     * @param theActivity name of activity
     */
    hasCollisionPerActivity(theId, theActivity) {
        return CollisionUtils._myCollisionUtils.hasCollisionPerActivity(theId, theActivity);
    }
    /**
     * Returns true if object has collision data
     * @param theId id of object
     */
    hasCollision(theId) { return this._myCollision.has(theId); }
    /**
     * Returns number of collisions.
     * @param theId id of object
     * @param theActivity name of activity
     */
    numberOfCollisionsPerActivity(theId, theActivity) {
        return CollisionUtils._myCollisionUtils.numberOfCollisionsPerActivity(theId, theActivity);
    }
    /**
     * Returns number of collisions.
     * @param theId id of object
     */
    numberOfCollisions(theId) {
        return CollisionUtils._myCollisionUtils.numberOfCollisions(theId);
    }
    /**
   * Returns number of Steps.
   * @param theId id of object
   * @param theActivity name of activity
   */
    numberOfStepsPerActivity(theId, theActivity) {
        return CollisionUtils._myCollisionUtils.numberOfStepsPerActivity(theId, theActivity);
    }
    /**
     * Returns arrays of VideoClue.
     * @param theId id of object
     * @param theActivity name of activity
     * @param theDuration: duration of video
     */
    getVideoClues(theId, theActivity, theDuration) {
        return cppToTsVector(CollisionUtils._myCollisionUtils.getVideoClues(theId, theActivity, theDuration));
    }
    /**
     * Returns collision perecentage.
     * @param theId id of object
     * @param theActivity name of activity
     */
    getActivityPerecentage(theId, theActivity) {
        return CollisionUtils._myCollisionUtils.getActivityPerecentage(theId, theActivity);
    }
    /**
   * Returns array of steps with collisions.
   * @param theId id of object
   * @param theActivity name of activity
   */
    getStepsWithCollision(theId, theActivity) {
        return cppToTsVector(CollisionUtils._myCollisionUtils.getStepsWithCollision(theId, theActivity));
    }
    /**
     * Set Viewer
     * @internal
     */
    static setViewer(theViewer) {
        this._myViewer = theViewer;
        this._myMedicalUtils = new MedicalUtils(this._myViewer);
        this._myCollisionUtils = getMedical(theViewer).collision();
    }
}
CollisionUtils._myViewer = null;
/**
 * Set viewer on Init
 * @internal
 */
OccViewer.onInit((theModule) => {
    CollisionUtils.setViewer(theModule);
});
